'use client';

import type { FC } from 'react';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { createTranslations } from '@/i18n/types';
import { redirectToLogin } from '../utils/redirectToLogin';

const translations = createTranslations({
  en: {
    firstSkinTitleA: 'First skin —',
    firstSkinTitleB: 'free',
    noDeposit: 'No deposit required',
    realSkin: 'Real skin on your Steam account',
    availableForAll: 'Available to every new player',
    claimFreeSkin: 'Claim free skin',
    bonusTitle: 'Bonus',
    bonusPercent: '+100%',
    bonusSubtitle: 'Top up your balance for the first time — we double the amount.',
    promoLabel: 'Promocode to activate the bonus',
    promoAction: 'Log in to get the code',
  },
  ru: {
    firstSkinTitleA: 'Первый скин —',
    firstSkinTitleB: 'бесплатно',
    noDeposit: 'Без пополнения счёта',
    realSkin: 'Реальный скин на Steam аккаунт',
    availableForAll: 'Доступно каждому новому игроку',
    claimFreeSkin: 'Забрать бесплатный скин',
    bonusTitle: 'Бонус',
    bonusPercent: '+100%',
    bonusSubtitle: 'Пополни баланс впервые — и мы удвоим сумму.',
    promoLabel: 'Промокод для активации бонуса',
    promoAction: 'Войти, чтобы получить код',
  },
  uz: {
    firstSkinTitleA: 'Birinchi skin —',
    firstSkinTitleB: 'bepul',
    noDeposit: 'Hisobni toʻldirishsiz',
    realSkin: 'Steam akkauntga haqiqiy skin',
    availableForAll: 'Har bir yangi oʻyinchi uchun',
    claimFreeSkin: 'Bepul skinni olish',
    bonusTitle: 'Bonus',
    bonusPercent: '+100%',
    bonusSubtitle: 'Balansni birinchi marta toʻldiring — biz summani ikki barobar oshiramiz.',
    promoLabel: 'Bonusni faollashtirish uchun promokod',
    promoAction: 'Kodni olish uchun kiring',
  },
  fa: {
    firstSkinTitleA: 'اولین اسکین —',
    firstSkinTitleB: 'رایگان',
    noDeposit: 'بدون نیاز به شارژ',
    realSkin: 'اسکین واقعی روی حساب استیمت',
    availableForAll: 'برای هر بازیکن تازه',
    claimFreeSkin: 'اسکین رایگان را بگیر',
    bonusTitle: 'بونوس',
    bonusPercent: '+100%',
    bonusSubtitle: 'اولین بار موجودیت را شارژ کن — مبلغ را دو برابر می‌کنیم.',
    promoLabel: 'کد تخفیف برای فعال کردن بونوس',
    promoAction: 'برای گرفتن کد وارد شو',
  },
});

const CheckIcon: FC = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

export const PromoBanner: FC = () => {
  const { t } = useLocalTranslation(translations);

  return (
    <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {/* Left card — free first skin */}
      <button
        type="button"
        onClick={redirectToLogin}
        className="group text-start relative rounded-2xl p-6 sm:p-8 border border-white/[0.06] bg-[linear-gradient(135deg,rgba(16,22,30,0.9),rgba(8,12,18,0.95))] overflow-hidden transition-transform hover:scale-[1.005] cursor-pointer"
      >
        <h2 className="text-2xl sm:text-3xl font-bold leading-tight text-white mb-4">
          {t('firstSkinTitleA')}{' '}
          <span
            style={{
              background: 'linear-gradient(90deg,#34d399 0%,#22d3ee 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {t('firstSkinTitleB')}
          </span>
        </h2>

        <ul className="flex flex-col gap-2 text-white/70 text-sm sm:text-base mb-6">
          <li className="flex items-center gap-2 text-emerald-400/90">
            <CheckIcon /> <span className="text-white/80">{t('noDeposit')}</span>
          </li>
          <li className="flex items-center gap-2 text-emerald-400/90">
            <CheckIcon /> <span className="text-white/80">{t('realSkin')}</span>
          </li>
          <li className="flex items-center gap-2 text-emerald-400/90">
            <CheckIcon /> <span className="text-white/80">{t('availableForAll')}</span>
          </li>
        </ul>

        <div
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-black font-semibold text-sm sm:text-base"
          style={{ background: 'linear-gradient(90deg,#34d399 0%,#22d3ee 100%)' }}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M12 2 2 7l10 5 10-5-10-5zm0 7.27L4.21 5.3 12 9.27 19.79 5.3 12 9.27zM2 17l10 5 10-5v-7l-10 5-10-5v7z" />
          </svg>
          {t('claimFreeSkin')}
        </div>
      </button>

      {/* Right card — bonus +100% */}
      <button
        type="button"
        onClick={redirectToLogin}
        className="group text-start relative rounded-2xl p-6 sm:p-8 border border-white/[0.06] bg-[linear-gradient(135deg,rgba(10,14,22,0.95),rgba(6,8,14,0.98))] overflow-hidden transition-transform hover:scale-[1.005] cursor-pointer"
      >
        <h2 className="text-2xl sm:text-3xl font-bold leading-tight text-white mb-3">
          {t('bonusTitle')}{' '}
          <span
            style={{
              background: 'linear-gradient(90deg,#fde68a 0%,#f97316 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {t('bonusPercent')}
          </span>
        </h2>

        <p className="text-white/55 text-sm sm:text-base mb-6 max-w-[520px]">
          {t('bonusSubtitle')}
        </p>

        <div className="flex flex-col gap-2">
          <span className="text-white/40 text-xs sm:text-sm">{t('promoLabel')}</span>
          <div
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-amber-500/40 bg-black/30 text-amber-200/80 text-sm font-medium w-fit"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span className="tracking-wider">{t('promoAction')}</span>
          </div>
        </div>
      </button>
    </section>
  );
};
